import React, { useState, useEffect } from 'react';
import {
  FiDollarSign, FiTrendingUp, FiCalendar, FiBarChart2,
  FiShoppingBag,
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import adminService from '../../services/admin.service';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Bar chart
const BarChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted py-5">
        <FiBarChart2 size={40} className="mb-2 d-block mx-auto" />
        <p>No revenue data yet. Revenue will appear here once orders are paid.</p>
      </div>
    );
  }

  const maxRevenue = Math.max(...data.map((d) => parseFloat(d.revenue) || 0), 1);

  return (
    <div>
      <div className="d-flex align-items-end gap-1" style={{ height: '240px' }}>
        {data.map((day, idx) => {
          const revenue = parseFloat(day.revenue) || 0;
          const height = Math.max((revenue / maxRevenue) * 220, 2);
          const date = new Date(day.date);
          const isToday = new Date().toDateString() === date.toDateString();

          return (
            <div
              key={idx}
              className="d-flex flex-column align-items-center flex-grow-1"
              style={{ minWidth: 0, cursor: 'default' }}
              title={`${date.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}\nRevenue: ${formatCurrency(revenue)}\nOrders: ${day.orders}`}
            >
              <div
                style={{
                  width: '100%',
                  maxWidth: '28px',
                  height: `${height}px`,
                  background: isToday
                    ? 'linear-gradient(180deg, #EAB308, #CA8A04)'
                    : 'linear-gradient(180deg, #DC2626, #B91C1C)',
                  borderRadius: '4px 4px 0 0',
                  opacity: revenue === 0 ? 0.15 : 0.85,
                  transition: 'height 0.3s ease',
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="d-flex justify-content-between mt-2 px-1">
        <small className="text-muted">
          {new Date(data[0]?.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
        </small>
        <small className="text-muted fw-semibold">Last 30 days</small>
        <small className="text-muted">
          {new Date(data[data.length - 1]?.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
        </small>
      </div>
    </div>
  );
};

// Orders by status
const StatusBreakdown = ({ data }) => {
  if (!data || data.length === 0) {
    return <small className="text-muted">No orders yet</small>;
  }

  const statusConfig = {
    pending: { color: '#F59E0B', label: 'Pending' },
    confirmed: { color: '#3B82F6', label: 'Confirmed' },
    processing: { color: '#8B5CF6', label: 'Processing' },
    shipped: { color: '#06B6D4', label: 'Shipped' },
    delivered: { color: '#10B981', label: 'Delivered' },
    cancelled: { color: '#EF4444', label: 'Cancelled' },
    refunded: { color: '#6B7280', label: 'Refunded' },
  };

  const total = data.reduce((sum, s) => sum + parseInt(s.count), 0);

  return (
    <div>
      {/* Stacked bar */}
      <div className="d-flex rounded overflow-hidden mb-4" style={{ height: '16px' }}>
        {data.map((s, idx) => (
          <div
            key={idx}
            style={{
              width: `${(parseInt(s.count) / total) * 100}%`,
              backgroundColor: statusConfig[s.status]?.color || '#9CA3AF',
              minWidth: parseInt(s.count) > 0 ? '4px' : '0',
            }}
            title={`${s.status}: ${s.count}`}
          />
        ))}
      </div>

      {/* Status rows */}
      {data.map((s, idx) => {
        const config = statusConfig[s.status] || { color: '#9CA3AF', label: s.status };
        const percent = ((parseInt(s.count) / total) * 100).toFixed(1);

        return (
          <div key={idx} className="d-flex align-items-center justify-content-between py-2 border-bottom">
            <div className="d-flex align-items-center gap-2">
              <span
                className="rounded-circle d-inline-block"
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: config.color,
                  flexShrink: 0,
                }}
              />
              <span className="text-capitalize">{config.label}</span>
            </div>
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted small">{percent}%</span>
              <strong>{s.count}</strong>
            </div>
          </div>
        );
      })}

      <div className="d-flex justify-content-between pt-3">
        <strong>Total</strong>
        <strong>{total}</strong>
      </div>
    </div>
  );
};

// Top days table
const TopDays = ({ data }) => {
  if (!data || data.length === 0) return null;

  const sorted = [...data]
    .filter((d) => parseFloat(d.revenue) > 0)
    .sort((a, b) => parseFloat(b.revenue) - parseFloat(a.revenue))
    .slice(0, 5);

  if (sorted.length === 0) return null;

  return (
    <div>
      <h6 className="fw-bold mb-3">Top Revenue Days</h6>
      <table className="table table-sm mb-0">
        <thead>
          <tr>
            <th className="text-muted small fw-semibold">Date</th>
            <th className="text-muted small fw-semibold text-end">Revenue</th>
            <th className="text-muted small fw-semibold text-end">Orders</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((day, idx) => (
            <tr key={idx}>
              <td>
                {new Date(day.date).toLocaleDateString('en-IN', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </td>
              <td className="text-end fw-semibold">{formatCurrency(parseFloat(day.revenue))}</td>
              <td className="text-end">{day.orders}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminRevenuePage = () => {
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        const { data } = await adminService.getRevenue();
        setRevenue(data.data);
      } catch (error) {
        console.error('Error fetching revenue:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRevenue();
  }, []);

  if (loading) return <LoadingSpinner />;

  const totalRevenue = revenue?.totalRevenue || 0;
  const monthlyRevenue = revenue?.monthlyRevenue || 0;
  const dailyData = revenue?.dailyRevenue || [];
  const todayData = dailyData.length > 0 ? dailyData[dailyData.length - 1] : null;
  const todayRevenue = todayData ? parseFloat(todayData.revenue) : 0;
  const avgDaily = dailyData.length > 0
    ? dailyData.reduce((sum, d) => sum + parseFloat(d.revenue || 0), 0) / dailyData.length
    : 0;

  return (
    <div>
      <h2 className="mb-4">Revenue Analytics</h2>

      {/* Revenue Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-lg-3">
          <div className="dashboard-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{ width: '44px', height: '44px', background: 'rgba(185, 28, 28, 0.1)' }}
              >
                <FiDollarSign size={22} className="text-primary" />
              </div>
              <div>
                <div className="dashboard-label">Total Revenue</div>
                <div className="fw-bold fs-5">{formatCurrency(totalRevenue)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="dashboard-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{ width: '44px', height: '44px', background: 'rgba(234, 179, 8, 0.1)' }}
              >
                <FiCalendar size={22} style={{ color: '#EAB308' }} />
              </div>
              <div>
                <div className="dashboard-label">This Month</div>
                <div className="fw-bold fs-5">{formatCurrency(monthlyRevenue)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="dashboard-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{ width: '44px', height: '44px', background: 'rgba(16, 185, 129, 0.1)' }}
              >
                <FiTrendingUp size={22} className="text-success" />
              </div>
              <div>
                <div className="dashboard-label">Today</div>
                <div className="fw-bold fs-5">{formatCurrency(todayRevenue)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="dashboard-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{ width: '44px', height: '44px', background: 'rgba(99, 102, 241, 0.1)' }}
              >
                <FiBarChart2 size={22} style={{ color: '#6366F1' }} />
              </div>
              <div>
                <div className="dashboard-label">Avg. Daily</div>
                <div className="fw-bold fs-5">{formatCurrency(avgDaily)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart + Status */}
      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <div className="dashboard-card">
            <h6 className="fw-bold mb-3">Daily Revenue — Last 30 Days</h6>
            <BarChart data={dailyData} />
          </div>
        </div>
        <div className="col-lg-4">
          <div className="dashboard-card">
            <h6 className="fw-bold mb-3">Orders by Status</h6>
            <StatusBreakdown data={revenue?.ordersByStatus} />
          </div>
        </div>
      </div>

      {/* Top Days */}
      <div className="row g-3">
        <div className="col-lg-6">
          <div className="dashboard-card">
            <TopDays data={dailyData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRevenuePage;
